import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    // Stable logout function
    const logout = useCallback(async () => {
        console.log('Initiating logout...');
        setIsLoading(true);
        try {
            await authService.logout();
        } catch (err) {
            console.error('Logout API error:', err);
        } finally {
            authService.clearAuthData();
            setUser(null);
            setIsAuthenticated(false);
            setError(null);
            navigate('/login', { replace: true });
            setIsLoading(false);
        }
    }, [navigate]);

    // Stable checkAuth function
    const checkAuth = useCallback(async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('accessToken');
            const username = localStorage.getItem('username');

            if (!token || !username) {
                console.log("Missing token or username");
                return false;
            }

            // 1. Check if token is expired locally
            if (authService.isTokenExpired()) {
                console.log("Token expired locally, attempting refresh...");
                try {
                    const newToken = await authService.getValidAccessToken();
                    if (!newToken) {
                        console.log("Token refresh failed");
                        return false;
                    }
                } catch (refreshError) {
                    console.error("Refresh error:", refreshError);
                    return false;
                }
            }

            // 2. Verify with server
            try {
                const isValid = await authService.verifyAccessToken();
                if (!isValid) {
                    console.log("Server token verification failed");
                    return false;
                }
            } catch (verifyError) {
                console.error("Verification error:", verifyError);
                return false;
            }

            // Passed checks → update state
            setUser(username);
            setIsAuthenticated(true);
            return true;
        } catch (err) {
            console.error('Auth check failed:', err);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Initialize auth state (fixed)
    useEffect(() => {
        let isMounted = true;
        let refreshInterval;

        const initializeAuth = async () => {
            try {
                const authValid = await checkAuth();
                if (!authValid && isMounted) {
                    console.log("Initial auth check failed");
                    // ❌ Don't call logout() here (caused flicker loop)
                    authService.clearAuthData();
                    setUser(null);
                    setIsAuthenticated(false);
                    setError(null);
                    navigate('/login', { replace: true });
                }
            } catch (err) {
                console.error("Initialization error:", err);
            }
        };

        initializeAuth();

        if (isAuthenticated) {
            refreshInterval = setInterval(async () => {
                try {
                    console.log("Running scheduled token refresh...");
                    const newToken = await authService.getValidAccessToken();
                    if (!newToken) {
                        console.log("Refresh failed - keeping session until manual logout");
                    }
                } catch (err) {
                    console.error("Scheduled refresh error:", err);
                }
            }, 30 * 60 * 1000); // 30 minutes
        }

        return () => {
            isMounted = false;
            clearInterval(refreshInterval);
        };
    }, [isAuthenticated, checkAuth, navigate]);

    // Login function
    const login = async (credentials) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await authService.login(credentials);
            const userData = response.data;

            setUser(userData.username);
            setIsAuthenticated(true);

            return response;
        } catch (err) {
            setError(err.message || 'Login failed');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    // Track activity
    useEffect(() => {
        const handleActivity = () => {
            localStorage.setItem('lastActivity', Date.now());
        };

        const events = ['click', 'keypress', 'scroll', 'mousemove'];
        events.forEach(e => window.addEventListener(e, handleActivity));

        return () => {
            events.forEach(e => window.removeEventListener(e, handleActivity));
        };
    }, []);

    return (
        <AuthContext.Provider
            value={{
                user,
                isAuthenticated,
                isLoading,
                error,
                login,
                logout,
                checkAuth
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
